import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import './GlobalSearch.css';

interface SearchResult {
  q: string;
  results: {
    clients?: Array<{ id: number; name: string; company_name: string | null; email: string | null }>;
    projects?: Array<{ id: number; case_number: string; title: string; client_name: string; client_company_name: string | null }>;
    evidence?: Array<{ id: number; chain_of_custody_number: string; description: string; project_id: number }>;
    invoices?: Array<{ id: number; invoice_number: string; status: string; total: string; client_name: string }>;
    contractors?: Array<{ id: number; legal_name: string; business_name: string | null }>;
  };
}

export function GlobalSearch(): JSX.Element {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // Debounce-ish: query only fires when q is at least 2 chars; the
  // 200ms staleTime + the React-Query identity-key dedup gives us
  // a perceived "type to filter" without explicit setTimeouts.
  const { data } = useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) =>
      api.get<SearchResult>(`/api/search?q=${encodeURIComponent(q)}`, signal),
    enabled: q.trim().length >= 2,
    staleTime: 200,
  });

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQ('');
    navigate(path);
  };

  const r = data?.results;
  const hasResults =
    !!r &&
    ((r.clients?.length ?? 0)
      + (r.projects?.length ?? 0)
      + (r.evidence?.length ?? 0)
      + (r.invoices?.length ?? 0)
      + (r.contractors?.length ?? 0)) > 0;

  return (
    <div className="gs-wrap" ref={ref}>
      <input
        type="search"
        className="form-input gs-input"
        placeholder="Search…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        aria-label="Search"
      />
      {open && q.trim().length >= 2 && (
        <div className="gs-dropdown">
          {!hasResults && <div className="gs-empty">No matches</div>}
          {r?.projects && r.projects.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Projects</div>
              {r.projects.map((p) => (
                <button
                  key={`p-${p.id}`}
                  type="button"
                  className="gs-item"
                  onClick={() => go(`/projects/${p.id}`)}
                >
                  <code className="gs-code">{p.case_number}</code>
                  <span className="gs-item-title">{p.title}</span>
                  <span className="gs-item-meta">{p.client_company_name ?? p.client_name}</span>
                </button>
              ))}
            </div>
          )}
          {r?.clients && r.clients.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Clients</div>
              {r.clients.map((c) => (
                <button
                  key={`c-${c.id}`}
                  type="button"
                  className="gs-item"
                  onClick={() => go(`/clients/${c.id}`)}
                >
                  <span className="gs-item-title">{c.name}</span>
                  {c.email && <span className="gs-item-meta">{c.email}</span>}
                </button>
              ))}
            </div>
          )}
          {r?.evidence && r.evidence.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Evidence</div>
              {r.evidence.map((e) => (
                <button
                  key={`e-${e.id}`}
                  type="button"
                  className="gs-item"
                  onClick={() => go(`/evidence/${e.id}`)}
                >
                  <code className="gs-code">{e.chain_of_custody_number}</code>
                  <span className="gs-item-title">{e.description}</span>
                </button>
              ))}
            </div>
          )}
          {r?.invoices && r.invoices.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Invoices</div>
              {r.invoices.map((iv) => (
                <button
                  key={`i-${iv.id}`}
                  type="button"
                  className="gs-item"
                  onClick={() => go(`/invoices/${iv.id}`)}
                >
                  <code className="gs-code">{iv.invoice_number}</code>
                  <span className="gs-item-title">{iv.client_name}</span>
                  <span className="gs-item-meta">{iv.status}</span>
                </button>
              ))}
            </div>
          )}
          {r?.contractors && r.contractors.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Contractors</div>
              {r.contractors.map((c) => (
                <button
                  key={`ct-${c.id}`}
                  type="button"
                  className="gs-item"
                  onClick={() => go('/contractors')}
                >
                  <span className="gs-item-title">{c.legal_name}</span>
                  {c.business_name && <span className="gs-item-meta">{c.business_name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
